// The one HTTP client of the dashboard (spec/dashboard.md section 1.2). Every request names an operation of the registry;
// the client applies the API conventions (bearer, request id, JSON, the error envelope, If-Match, Idempotency-Key,
// If-None-Match with a per-URL ETag cache) and returns the parsed body with the headers the workspace needs.
import { ops, type BodyOf, type Method, type Operation, type PathParamsOf, type QueryOf, type ResponseOf } from './operations';
import type { paths } from './schema';
import { openStream, type StreamFrame, type StreamState, type StreamStateInfo } from './stream';
import type { ErrorEnvelope, Page, Session } from './types';

/** The one error type the dashboard ever sees: the server's envelope, or a transport failure mapped to the same shape. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId: string,
    public readonly details: ErrorEnvelope['error']['details'] = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Message for one field, when the server reported it. */
  fieldMessage(field: string): string | undefined {
    return this.details?.find((d) => d.field === field)?.message;
  }

  /** A stale If-Match / ifVersion: the server refused to overwrite someone else's write (section 6). */
  get isVersionConflict(): boolean {
    return this.status === 412 || this.code === 'precondition_failed';
  }
}

export type QueryValue = string | number | boolean | null | undefined | ReadonlyArray<string | number | boolean>;
export type Query = Record<string, QueryValue>;

export interface CallOptions<O> {
  path?: PathParamsOf<O>;
  /** typed by the operation's declared query parameters (spec/api-conventions.md section 21.1) */
  query?: QueryOf<O>;
  body?: BodyOf<O>;
  ifMatch?: string | number | null;
  idempotencyKey?: string;
  /** false for the sign-in routes */
  auth?: boolean;
  /** send If-None-Match from the ETag cache and answer the cached body on 304 */
  cached?: boolean;
  signal?: AbortSignal;
  /** a non-JSON body (an asset upload): sent as is with `contentType` */
  rawBody?: BodyInit;
  contentType?: string;
}

export interface CallResult<T> {
  data: T;
  status: number;
  etag: string | null;
  /** X-Huelency-Warning: a non-blocking warning of the write (a near-duplicate template name, Q-D-27) */
  warning: string | null;
  requestId: string | null;
  /** X-Huelency-Commit: the branch head after a content read or write */
  commit: string | null;
  /** true when a cached body answered a 304 */
  fromCache: boolean;
}

export interface ClientOptions {
  baseUrl?: string;
  getToken: () => string | null;
  onUnauthenticated?: () => void;
  fetchImpl?: typeof fetch;
}

function newRequestId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** Substitutes `{name}` segments (percent-encoded; a `/` inside a branch name travels as %2F). */
export function fillPath(template: string, params: Record<string, string> = {}): string {
  return template.replace(/\{([^}]+)\}/g, (_, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`missing path parameter '${name}' for ${template}`);
    return encodeURIComponent(value);
  });
}

/** Serialises a query: undefined / null dropped, arrays repeated. */
export function encodeQuery(query: Query | undefined): string {
  if (!query) return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    const values = Array.isArray(value) ? value : [value];
    for (const v of values) parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? '?' + parts.join('&') : '';
}

export function etagOfVersion(version: string | number | null | undefined): string | undefined {
  if (version === null || version === undefined || version === '') return undefined;
  return `"v${version}"`;
}

interface CacheEntry {
  etag: string;
  body: unknown;
  commit: string | null;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly etags = new Map<string, CacheEntry>();
  /** the X-Request-Id of the recent requests: an event whose requestId is one of them is this client's own write */
  private readonly sent = new Set<string>();

  constructor(private readonly options: ClientOptions) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
  }

  /** The URL of an operation, for logs and the deep links the pages build. */
  url<O extends Operation<keyof paths, Method>>(operation: O, path?: PathParamsOf<O>, query?: Query): string {
    return this.baseUrl + fillPath(operation.path, (path ?? {}) as Record<string, string>) + encodeQuery(query);
  }

  async call<O extends Operation<keyof paths, Method>>(operation: O, opts: CallOptions<O> = {}): Promise<CallResult<ResponseOf<O>>> {
    const relative = fillPath(operation.path, (opts.path ?? {}) as Record<string, string>) + encodeQuery(opts.query as Query | undefined);
    return this.request<ResponseOf<O>>(operation.method, relative, opts.body, opts);
  }

  /**
   * The raw request under `call`. Pages never use it (the registry is the contract); the client's own tests do.
   */
  async request<T>(
    method: string,
    relative: string,
    body?: unknown,
    opts: { ifMatch?: string | number | null; idempotencyKey?: string; auth?: boolean; cached?: boolean; signal?: AbortSignal; rawBody?: BodyInit; contentType?: string } = {},
  ): Promise<CallResult<T>> {
    const requestId = newRequestId();
    if (method !== 'GET') {
      this.sent.add(requestId);
      if (this.sent.size > 500) this.sent.delete(this.sent.values().next().value as string);
    }
    const headers: Record<string, string> = { Accept: 'application/json', 'X-Request-Id': requestId };
    if (opts.rawBody !== undefined) headers['Content-Type'] = opts.contentType ?? 'application/octet-stream';
    else if (body !== undefined) headers['Content-Type'] = 'application/json';
    const token = opts.auth === false ? null : this.options.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
    const ifMatch = typeof opts.ifMatch === 'string' ? opts.ifMatch : etagOfVersion(opts.ifMatch);
    if (ifMatch) headers['If-Match'] = ifMatch;
    const cacheKey = method === 'GET' && opts.cached ? relative + (token ?? '') : null;
    const cached = cacheKey ? this.etags.get(cacheKey) : undefined;
    if (cached) headers['If-None-Match'] = cached.etag;

    let response: Response;
    try {
      response = await this.fetchImpl(this.baseUrl + relative, {
        method,
        headers,
        body: opts.rawBody !== undefined ? opts.rawBody : body === undefined ? undefined : JSON.stringify(body),
        signal: opts.signal,
      });
    } catch (e) {
      if ((e as Error).name === 'AbortError') throw e;
      throw new ApiError(0, 'unavailable', `Network error: ${(e as Error).message}`, requestId);
    }

    const responseId = response.headers.get('X-Request-Id');
    const etag = response.headers.get('ETag');
    const commit = response.headers.get('X-Huelency-Commit');
    const warning = response.headers.get('X-Huelency-Warning');
    if (response.status === 304 && cached) {
      return { data: cached.body as T, status: 304, etag: cached.etag, warning, requestId: responseId, commit: commit ?? cached.commit, fromCache: true };
    }
    if (response.status === 204 || response.status === 304) {
      return { data: undefined as T, status: response.status, etag, warning, requestId: responseId, commit, fromCache: false };
    }
    const text = await response.text();
    let parsed: unknown = null;
    if (text.length > 0) {
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new ApiError(response.status, 'internal_error', 'The server returned a non-JSON response.', responseId ?? requestId);
      }
    }
    if (!response.ok) {
      const envelope = parsed as ErrorEnvelope | null;
      const err = envelope?.error;
      if (err?.code === 'unauthenticated') this.options.onUnauthenticated?.();
      throw new ApiError(response.status, err?.code ?? 'internal_error', err?.message ?? `HTTP ${response.status}`, err?.requestId ?? responseId ?? requestId, err?.details ?? null);
    }
    if (cacheKey && etag) this.etags.set(cacheKey, { etag, body: parsed, commit });
    return { data: parsed as T, status: response.status, etag, warning, requestId: responseId, commit, fromCache: false };
  }

  /** True when `requestId` is the X-Request-Id of a write this client sent (an event that is its own echo). */
  sentRequest(requestId: string | null | undefined): boolean {
    return !!requestId && this.sent.has(requestId);
  }

  /**
   * Reads an event stream of the registry (`streamGameEvents`, `streamJobEvents`; spec/api-conventions.md section 21.5)
   * with the bearer, resuming with Last-Event-ID and backing off between attempts (`src/api/stream.ts`). Resolves when
   * the stream stopped (aborted, or refused for good: the page keeps its poll).
   */
  stream<O extends Operation<keyof paths, Method>>(
    operation: O,
    opts: {
      path?: PathParamsOf<O>;
      query?: QueryOf<O>;
      lastEventId?: string | null;
      signal: AbortSignal;
      onFrame: (frame: StreamFrame) => void;
      onStateChange?: (state: StreamState, info?: StreamStateInfo) => void;
      sleep?: (ms: number, signal: AbortSignal) => Promise<void>;
    },
  ): Promise<void> {
    const relative = fillPath(operation.path, (opts.path ?? {}) as Record<string, string>) + encodeQuery(opts.query as Query | undefined);
    return openStream({
      url: this.baseUrl + relative,
      token: this.options.getToken,
      lastEventId: opts.lastEventId,
      signal: opts.signal,
      onFrame: opts.onFrame,
      onStateChange: opts.onStateChange,
      onUnauthenticated: this.options.onUnauthenticated,
      fetchImpl: this.fetchImpl,
      sleep: opts.sleep,
    });
  }

  /**
   * A GET of the registry whose answer is a file, not JSON (an export's gzip NDJSON, `downloadJobResult`): sent with the
   * bearer like every call (a plain link cannot carry it), answered as a Blob with the file name of `Content-Disposition`;
   * a refusal is parsed as the envelope and thrown as an ApiError.
   */
  async blob<O extends Operation<keyof paths, Method>>(operation: O, opts: { path?: PathParamsOf<O>; query?: QueryOf<O>; signal?: AbortSignal } = {}): Promise<{ blob: Blob; filename: string | null; requestId: string | null }> {
    const relative = fillPath(operation.path, (opts.path ?? {}) as Record<string, string>) + encodeQuery(opts.query as Query | undefined);
    const requestId = newRequestId();
    const headers: Record<string, string> = { Accept: '*/*', 'X-Request-Id': requestId };
    const token = this.options.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    let response: Response;
    try {
      response = await this.fetchImpl(this.baseUrl + relative, { method: operation.method, headers, signal: opts.signal });
    } catch (e) {
      if ((e as Error).name === 'AbortError') throw e;
      throw new ApiError(0, 'unavailable', `Network error: ${(e as Error).message}`, requestId);
    }
    const responseId = response.headers.get('X-Request-Id');
    if (!response.ok) {
      let envelope: ErrorEnvelope | null = null;
      try {
        envelope = (await response.json()) as ErrorEnvelope;
      } catch {
        envelope = null;
      }
      const err = envelope?.error;
      if (err?.code === 'unauthenticated') this.options.onUnauthenticated?.();
      throw new ApiError(response.status, err?.code ?? 'internal_error', err?.message ?? `HTTP ${response.status}`, err?.requestId ?? responseId ?? requestId, err?.details ?? null);
    }
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent((match[1] ?? match[2] ?? '').trim()) : null;
    return { blob: await response.blob(), filename: filename || null, requestId: responseId };
  }

  /** `call` reduced to its body. */
  data<O extends Operation<keyof paths, Method>>(operation: O, opts: CallOptions<O> = {}): Promise<ResponseOf<O>> {
    return this.call(operation, opts).then((r) => r.data);
  }

  /** A raw GET by path (the client's own tests; pages use the registry). */
  get<T>(relative: string): Promise<T> {
    return this.request<T>('GET', relative).then((r) => r.data);
  }

  /**
   * Follows `nextCursor` to the end, or to `max` items when the caller sets one. No cap by default: the branch catalogs
   * (strings, assets, products, templates) are every row of the branch, and a cut list would hide rows without a word.
   */
  async all<T>(fetchPage: (cursor: string | null) => Promise<Page<T>>, max = Number.POSITIVE_INFINITY): Promise<T[]> {
    const items: T[] = [];
    let cursor: string | null = null;
    do {
      const page: Page<T> = await fetchPage(cursor);
      items.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor && items.length < max);
    return items;
  }

  // ---- the conveniences the M0 pages use (every one goes through the registry)

  signIn(email: string, password: string): Promise<Session> {
    return this.data(ops.signIn, { body: { email, password }, auth: false });
  }

  signUp(email: string, password: string, displayName: string): Promise<Session> {
    return this.data(ops.signUp, { body: { email, password, displayName }, auth: false });
  }

  signOut(): Promise<void> {
    return this.call(ops.signOut).then(() => undefined);
  }

  me() {
    return this.data(ops.getMe);
  }

  organizations() {
    return this.data(ops.listOrganizations, { query: { limit: 200 } });
  }

  organization(org: string) {
    return this.data(ops.getOrganization, { path: { org } });
  }

  createOrganization(body: { slug: string; name: string }, idempotencyKey: string) {
    return this.data(ops.createOrganization, { body, idempotencyKey });
  }

  games(org: string, includeArchived = false) {
    return this.data(ops.listGames, { path: { org }, query: { limit: 200, includeArchived: includeArchived ? 'true' : undefined } });
  }

  game(org: string, game: string) {
    return this.data(ops.getGame, { path: { org, game } });
  }

  createGame(org: string, body: { slug: string; name: string; description?: string }, idempotencyKey: string) {
    return this.data(ops.createGame, { path: { org }, body: { slug: body.slug, name: body.name, ...(body.description !== undefined ? { description: body.description } : {}) }, idempotencyKey });
  }

  environments(org: string, game: string) {
    return this.data(ops.listEnvironments, { path: { org, game } });
  }

  branches(org: string, game: string) {
    return this.data(ops.listBranches, { path: { org, game }, query: { limit: 200 } });
  }

  audit(org: string, game: string, limit = 50) {
    return this.data(ops.listGameAudit, { path: { org, game }, query: { limit } });
  }

  job(org: string, game: string, job: string) {
    return this.data(ops.getJob, { path: { org, game, job } });
  }
}
